import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { Wallet } from "lucide-react";

export const AccountDocs = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Account Overview</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Understanding your account balances, margin, and account health metrics</p>
        </div>

        {/* Balances Panel */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Understanding the Balances Panel</h2>
          
          <p className="text-muted-foreground mb-6">
            The Balances (Account) panel provides a comprehensive view of your account status. 
            You can add it to your layout by clicking the "+" button and selecting "Balances" or "Account".
          </p>

          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Wallet className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Total Collateral</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                The total amount of USDC you've deposited into your trading account. This is your base 
                account balance before considering open positions.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Available Balance</h3>
              <p className="text-sm text-muted-foreground mb-2">
                The amount of collateral available to open new positions or add to existing ones.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm font-mono">
                Available = Total Collateral - Used Margin + Unrealized P&L
              </div>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Used Margin</h3>
              <p className="text-sm text-muted-foreground">
                The amount of collateral currently locked in your open positions. This cannot be withdrawn 
                while positions are open.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Unrealized P&L</h3>
              <p className="text-sm text-muted-foreground">
                Total profit or loss from all open positions. This value changes in real-time as market 
                prices move. Green means profit, red means loss.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Total Equity</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Your current account value including unrealized P&L. This is what your account would be 
                worth if you closed all positions now.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm font-mono">
                Equity = Total Collateral + Unrealized P&L
              </div>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Account Leverage</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Your overall account leverage across all positions.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm font-mono">
                Account Leverage = Total Position Value / Total Equity
              </div>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Margin Ratio</h3>
              <p className="text-sm text-muted-foreground mb-2">
                The most important metric for account health. Shows how close you are to liquidation.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm font-mono mb-3">
                Margin Ratio = (Total Equity / Used Margin) × 100%
              </div>
              <div className="space-y-2 text-sm">
                <div className="p-2 bg-emerald-500/10 rounded">
                  <strong className="text-emerald-500">{'>'}= 250%:</strong> Safe -- normal trading
                </div>
                <div className="p-2 bg-blue-500/10 rounded">
                  <strong className="text-blue-500">150-250%:</strong> Caution -- monitor closely
                </div>
                <div className="p-2 bg-amber-500/10 rounded">
                  <strong className="text-amber-500">110-150%:</strong> Warning -- take action
                </div>
                <div className="p-2 bg-red-500/10 rounded">
                  <strong className="text-red-500">{'<'}= 100%:</strong> Liquidation -- positions closed
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Account Page */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Account Page</h2>
          
          <p className="text-muted-foreground mb-6">
            The Account page (accessible via the header menu) provides a comprehensive view of your 
            trading activity and account status.
          </p>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Account Overview</h3>
              <p className="text-sm text-muted-foreground">
                Quick snapshot of your Total Equity, Available Balance, P&L, and Margin Ratio.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Open Positions</h3>
              <p className="text-sm text-muted-foreground">
                All your currently open positions with detailed information and quick close buttons.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Trade History</h3>
              <p className="text-sm text-muted-foreground">
                Historical record of all your closed positions, including entry/exit prices, P&L, 
                fees, and execution times.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Funding History</h3>
              <p className="text-sm text-muted-foreground">
                Record of all funding payments you've paid or received on perpetual positions.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Transaction History</h3>
              <p className="text-sm text-muted-foreground">
                Complete log of deposits, withdrawals, and other account transactions.
              </p>
            </div>
          </div>
        </section>

        {/* Monitoring Account Health */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Monitoring Account Health</h2>
          
          <p className="text-muted-foreground mb-6">
            Regularly checking your account health is crucial for avoiding liquidation and managing risk:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li><strong className="text-foreground">Check daily</strong> — Review your account at least once per day, especially your Margin Ratio and Unrealized P&L.</li>
            <li><strong className="text-foreground">Watch margin ratio</strong> — If it drops below 150%, consider adding collateral or reducing positions to avoid liquidation risk.</li>
            <li><strong className="text-foreground">Keep buffer</strong> — Don&apos;t use 100% of your available balance. Keep at least 20-30% as a buffer for market volatility.</li>
            <li><strong className="text-foreground">Track performance</strong> — Review your trade history regularly to identify what strategies work and what doesn&apos;t.</li>
          </ul>
        </section>

        <DocsNavigation 
          previous={{ title: "Fee Structure", href: "/fees" }}
          next={{ title: "Charts & Analysis Tools", href: "/charts" }}
        />
      </div>
    </DocsLayout>
  );
};

